import React, { useState, useEffect } from 'react';
import { Outlet, useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { toast } from '../utils/toast';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { LoadingState } from '../components/ui/LoadingState';
import {
  LayoutDashboard,
  BookOpen,
  Package,
  ArrowLeft,
  Copy,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Globe,
  Radio,
  Terminal,
  BarChart3,
  LifeBuoy,
  ChevronRight,
  ChevronDown,
  Plus,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { ApiKeyDiagnosticModal } from '../components/ApiKeyDiagnosticModal';
import type { Application, Subscription, Product } from '../types';

export const ApplicationLayout: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { client } = useAuthStore();
  const { environment } = useEnvironment();
  const location = useLocation();
  const navigate = useNavigate();

  // State for copying and revealing API Key & App ID
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isRotatingKey, setIsRotatingKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedAppId, setCopiedAppId] = useState(false);
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const queryClient = useQueryClient();

  // Fetch all applications for current client
  const { data: allApplications = [] } = useQuery({
    queryKey: ['client-all-applications', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];
      const res = await api.get<{ data: Application[] }>(
        `/clients/${client.id}/applications`
      );
      return res.data?.data || [];
    },
    enabled: !!client?.id,
  });

  const envApplications = allApplications.filter(
    (app) => app.sandbox_mode === (environment === 'sandbox')
  );

  const handleSwitchApplication = (targetAppId: string) => {
    setShowAppSwitcher(false);
    if (targetAppId === id) return;

    const currentPath = location.pathname;
    const match = currentPath.match(new RegExp(`^/applications/${id}(/.*)?$`));
    const subPath = match && match[1] ? match[1] : '';

    navigate(`/applications/${targetAppId}${subPath}`);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowAppSwitcher(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch specific application
  const {
    data: appData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['application', id, environment],
    queryFn: async () => {
      const response = await api.get<{ data: Application }>(
        `/clients/${client?.id}/applications/${id}`
      );
      return response.data.data;
    },
    enabled: !!client?.id && !!id,
    retry: 1,
  });

  // Fetch subscriptions count for badge
  const { data: subsData } = useQuery<{ success: boolean; data: Subscription[] }>({
    queryKey: ['application-subs-count', id, client?.id, environment],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Subscription[] }>(
        `/clients/${client?.id}/subscriptions?limit=100`
      );
      return response.data;
    },
    enabled: !!client?.id && !!id,
  });

  // Fetch products count for catalog badge
  const { data: productsData } = useQuery<{ data: Product[] }>({
    queryKey: ['products-catalog-count'],
    queryFn: async () => (await api.get<{ data: Product[] }>('/products')).data,
  });

  const activeSubscriptions = (subsData?.data || []).filter(
    (s) => s.application_id === id && s.status === 'active'
  );
  const activeSubsCount = activeSubscriptions.length;
  const productsCount = productsData?.data?.length || 0;

  useEffect(() => {
    if (appData && appData.sandbox_mode !== (environment === 'sandbox')) {
      navigate('/applications');
    }
  }, [appData, environment, navigate]);

  const handleCopyAppId = async () => {
    if (!appData?.id) return;
    try {
      await navigator.clipboard.writeText(appData.id);
      setCopiedAppId(true);
      setTimeout(() => setCopiedAppId(false), 2000);
      toast.success('ID da aplicação copiado!');
    } catch {
      toast.error('Erro ao copiar ID da aplicação.');
    }
  };

  const toggleRevealKey = async () => {
    if (revealedKey) {
      setRevealedKey(null);
      return;
    }
    try {
      setIsRevealing(true);
      const res = await api.get<{ success: boolean; data: { api_key: string } }>(
        `/clients/${client?.id}/applications/${id}/api-key`
      );
      if (res.data?.data?.api_key) {
        setRevealedKey(res.data.data.api_key);
      }
    } catch {
      toast.error('Não foi possível obter a chave de API completa.');
    } finally {
      setIsRevealing(false);
    }
  };

  const handleCopyApiKey = async () => {
    try {
      let keyToCopy = revealedKey;
      if (!keyToCopy) {
        setIsRevealing(true);
        const res = await api.get<{ success: boolean; data: { api_key: string } }>(
          `/clients/${client?.id}/applications/${id}/api-key`
        );
        keyToCopy = res.data?.data?.api_key;
        if (keyToCopy) setRevealedKey(keyToCopy);
      }
      if (keyToCopy) {
        await navigator.clipboard.writeText(keyToCopy);
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
        toast.success('Chave de API copiada para a área de transferência!');
      }
    } catch {
      toast.error('Erro ao copiar a chave de API.');
    } finally {
      setIsRevealing(false);
    }
  };

  const handleRotateApiKey = async () => {
    if (!client?.id || !id || !appData) return;

    const result = await Swal.fire({
      title: 'Rotacionar Chave de API?',
      html: `
        <div style="text-align:left;margin-top:0.5rem">
          <p style="margin-bottom:0.75rem;font-size:0.875rem">
            Está prestes a gerar uma nova chave de API para a aplicação <strong>${appData.name}</strong>.
          </p>
          <div style="background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;padding:0.75rem;margin-bottom:0.75rem">
            <p style="color:#92400E;font-weight:700;margin-bottom:0.25rem;font-size:0.8125rem">⚠️ Atenção à Produção:</p>
            <ul style="color:#92400E;margin:0;padding-left:1.25rem;font-size:0.75rem">
              <li>A chave de API anterior será <strong>invalidada imediatamente</strong> no Kong Gateway.</li>
              <li>Sistemas e integrações com a chave antiga começarão a receber HTTP 401 Unauthorized.</li>
              <li>Deverá atualizar as variáveis de ambiente das suas aplicações com a nova chave.</li>
            </ul>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sim, rotacionar chave',
      cancelButtonText: 'Cancelar',
      width: '500px',
      customClass: {
        popup: 'rounded-2xl shadow-xl',
      },
    });

    if (!result.isConfirmed) return;

    try {
      setIsRotatingKey(true);
      const response = await api.post<{ data: { api_key: string }; warning?: string }>(
        `/clients/${client.id}/applications/${id}/regenerate-key`
      );

      const newKey = response.data?.data?.api_key;
      if (newKey) {
        setRevealedKey(newKey);
        await queryClient.invalidateQueries({ queryKey: ['application', id] });
        await queryClient.invalidateQueries({ queryKey: ['application-api-key', client.id, id] });

        await Swal.fire({
          title: 'Nova Chave de API Gerada!',
          html: `
            <div style="text-align:left;margin-top:0.5rem">
              <p style="font-size:0.8125rem;color:#4B5563;margin-bottom:0.75rem">
                Copie e guarde a nova chave agora. A chave antiga foi revogada.
              </p>
              <div style="background:#F3F4F6;border:1px solid #E5E7EB;border-radius:8px;padding:0.75rem;font-family:monospace;font-size:0.75rem;word-break:break-all;color:#111827">
                ${newKey}
              </div>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#059669',
          confirmButtonText: 'Entendido, já guardei',
          width: '500px',
          customClass: {
            popup: 'rounded-2xl shadow-xl',
          },
        });

        toast.success('Chave de API rotacionada com sucesso!');
      }
    } catch {
      toast.error('Falha ao rotacionar a chave de API. Tente novamente.');
    } finally {
      setIsRotatingKey(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container-custom py-8">
        <LoadingState type="card" count={1} />
      </div>
    );
  }

  if (isError || !appData) {
    return (
      <div className="container-custom py-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Aplicação não encontrada
        </h2>
        <Link to="/applications" className="btn-base btn-primary">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar para Aplicações
        </Link>
      </div>
    );
  }

  const primaryTabs = [
    {
      name: 'Visão Geral',
      description: 'Configurações & Snippet',
      path: `/applications/${id}`,
      icon: LayoutDashboard,
      exact: true,
      badge: null,
    },
    {
      name: 'Catálogo de APIs',
      description: 'Explorar e subscrever',
      path: `/applications/${id}/catalog`,
      icon: BookOpen,
      exact: false,
      badge: productsCount > 0 ? `${productsCount}` : null,
      badgeClass: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    },
    {
      name: 'Minhas Subscrições',
      description: 'Planos e quotas ativas',
      path: `/applications/${id}/subscriptions`,
      icon: Package,
      exact: false,
      badge:
        activeSubsCount > 0 ? `${activeSubsCount} Ativa${activeSubsCount > 1 ? 's' : ''}` : null,
      badgeClass:
        'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-bold',
    },
    {
      name: 'Analytics & Métricas',
      description: 'Quotas, latência & logs',
      path: `/applications/${id}/analytics`,
      icon: BarChart3,
      exact: true,
      badge: null,
    },
  ];

  // Quick anchor navigation when on the Overview page
  const isOverview = location.pathname === `/applications/${id}`;

  // Link for Swagger testing (first subscribed product or catalog)
  const testerLink =
    activeSubscriptions.length > 0
      ? `/applications/${id}/products/${activeSubscriptions[0].product_id}?tab=documentation`
      : `/applications/${id}/catalog`;

  return (
    <div className="container-custom py-8 animate-fade-in">
      <Breadcrumbs className="mb-6">
        <BreadcrumbItem href="/">Dashboard</BreadcrumbItem>
        <BreadcrumbItem href="/applications">Aplicações</BreadcrumbItem>
        <BreadcrumbItem>{appData.name}</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Modern Supercharged Sticky Sidebar */}
        <aside className="w-full lg:w-72 shrink-0 lg:sticky lg:top-20 lg:max-h-[calc(100vh-5.5rem)] lg:overflow-y-auto custom-scrollbar space-y-4 pb-4 pr-1">
          {/* Application Summary Card */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
              {/* Header with live status badge and Application Switcher Dropdown */}
              <div className="relative">
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAppSwitcher((prev) => !prev)}
                    className="flex-1 text-left group p-1.5 -m-1.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-all border border-transparent hover:border-slate-200 dark:hover:border-slate-700/60"
                    title="Clique para alternar entre aplicações"
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className={`relative flex h-2.5 w-2.5 shrink-0 ${
                          appData.status === 'active' ? 'text-emerald-500' : 'text-slate-400'
                        }`}
                      >
                        {appData.status === 'active' && (
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        )}
                        <span
                          className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                            appData.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'
                          }`}
                        />
                      </span>
                      <h1
                        className="text-base font-bold text-slate-900 dark:text-white truncate tracking-tight group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
                        title={appData.name}
                      >
                        {appData.name}
                      </h1>
                      <ChevronDown
                        className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${
                          showAppSwitcher ? 'rotate-180 text-primary-600' : ''
                        }`}
                      />
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                          appData.sandbox_mode
                            ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300'
                            : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300'
                        }`}
                      >
                        {appData.sandbox_mode ? 'Sandbox' : 'Produção'}
                      </span>
                      <span className="text-[11px] text-slate-400">•</span>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        {new Date(appData.created_at).toLocaleDateString('pt-PT')}
                      </span>
                    </div>
                  </button>

                  <Link
                    to="/applications"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                    title="Ver todas as aplicações"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Link>
                </div>

                {/* Floating Application Switcher Menu */}
                {showAppSwitcher && (
                  <div className="absolute top-full left-0 right-0 mt-2 z-50 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-2 space-y-1 animate-scale-in">
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                      <span>
                        Aplicações ({appData.sandbox_mode ? 'Sandbox' : 'Produção'})
                      </span>
                      <span>{envApplications.length}</span>
                    </div>

                    <div className="max-h-56 overflow-y-auto custom-scrollbar py-1 space-y-0.5">
                      {envApplications.map((app) => {
                        const isCurrent = app.id === appData.id;
                        return (
                          <button
                            key={app.id}
                            type="button"
                            onClick={() => handleSwitchApplication(app.id)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all text-left ${
                              isCurrent
                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-950/50 dark:text-primary-300 font-bold'
                                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                            }`}
                          >
                            <span className="truncate mr-2">{app.name}</span>
                            {isCurrent && (
                              <Check className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                      <Link
                        to="/applications"
                        onClick={() => setShowAppSwitcher(false)}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/40 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Gerir / Criar Nova Aplicação</span>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Application ID Widget with 1-click Copy */}
              <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
                    ID da Aplicação
                  </span>
                  <span className="font-mono text-xs text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                    {appData.id}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleCopyAppId}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white dark:hover:bg-slate-700 transition-colors shrink-0"
                  title="Copiar ID da Aplicação"
                >
                  {copiedAppId ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {/* API Key Widget with Reveal & Copy */}
              <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
                    Chave de API (X-API-Key)
                  </span>
                  <span className="font-mono text-xs text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                    {revealedKey
                      ? revealedKey
                      : appData.api_key_prefix
                        ? `${appData.api_key_prefix}••••••••••••`
                        : '••••••••••••••••'}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={toggleRevealKey}
                    disabled={isRevealing}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                    title={revealedKey ? 'Ocultar Chave' : 'Revelar Chave'}
                  >
                    {isRevealing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : revealedKey ? (
                      <EyeOff className="w-3.5 h-3.5" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyApiKey}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                    title="Copiar Chave de API"
                  >
                    {copiedKey ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleRotateApiKey}
                    disabled={isRotatingKey}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                    title="Rotacionar Chave de API"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isRotatingKey ? 'animate-spin text-amber-600' : ''}`} />
                  </button>
                  <ApiKeyDiagnosticModal
                    applicationId={appData.id}
                    clientId={client?.id || ''}
                    applicationName={appData.name}
                    triggerButtonVariant="icon"
                  />
                </div>
              </div>
            </div>

            {/* Sidebar Navigation Links */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-3 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
              {/* Group 1: Aplicação & Integração */}
              <div>
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 block">
                  Aplicação & Integração
                </span>
                <nav className="flex flex-col gap-1 mt-1.5">
                  {primaryTabs.map((tab) => {
                    const isActive = tab.exact
                      ? location.pathname === tab.path
                      : location.pathname.startsWith(tab.path);
                    const Icon = tab.icon;

                    return (
                      <Link
                        key={tab.name}
                        to={tab.path}
                        className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-all border ${
                          isActive
                            ? 'bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-400 font-bold border-primary-200/80 dark:border-primary-800/80 shadow-xs ring-1 ring-primary-500/10'
                            : 'border-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon
                            className={`w-4 h-4 shrink-0 ${
                              isActive
                                ? 'text-primary-600 dark:text-primary-400'
                                : 'text-slate-400 dark:text-slate-500'
                            }`}
                          />
                          <div className="min-w-0">
                            <span className="text-sm block truncate leading-tight">{tab.name}</span>
                            <span className="text-[11px] font-normal text-slate-400 block truncate leading-tight mt-0.5">
                              {tab.description}
                            </span>
                          </div>
                        </div>

                        {tab.badge && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] shrink-0 ml-2 ${
                              tab.badgeClass ||
                              'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {tab.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Group 2: Segurança & Acesso */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 block">
                  Segurança & Acesso
                </span>
                <nav className="flex flex-col gap-0.5 mt-1.5">
                  <a
                    href={
                      isOverview
                        ? '#credenciais-externas'
                        : `/applications/${id}#credenciais-externas`
                    }
                    className="flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    <KeyRound className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">Credenciais de APIs Externas</span>
                  </a>

                  <a
                    href={isOverview ? '#origens-cors' : `/applications/${id}#origens-cors`}
                    className="flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">Configuração de CORS</span>
                  </a>

                  <a
                    href={isOverview ? '#webhooks' : `/applications/${id}#webhooks`}
                    className="flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    <Radio className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">Webhooks & Eventos</span>
                  </a>
                </nav>
              </div>

              {/* Group 3: Ferramentas de Developer */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 block">
                  Ferramentas
                </span>
                <nav className="flex flex-col gap-0.5 mt-1.5">
                  <Link
                    to={testerLink}
                    className="flex items-center justify-between px-3.5 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors group"
                  >
                    <div className="flex items-center gap-3 truncate">
                      <Terminal className="w-3.5 h-3.5 text-slate-400 group-hover:text-primary-500 shrink-0" />
                      <span className="truncate">Testar APIs (Swagger)</span>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 opacity-60 group-hover:opacity-100 shrink-0" />
                  </Link>

                  <Link
                    to="/analytics"
                    className="flex items-center justify-between px-3.5 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors group"
                  >
                    <div className="flex items-center gap-3 truncate">
                      <BarChart3 className="w-3.5 h-3.5 text-slate-400 group-hover:text-primary-500 shrink-0" />
                      <span className="truncate">Consumo & Analytics</span>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-400 opacity-60 group-hover:opacity-100 shrink-0" />
                  </Link>
                </nav>
              </div>
            </div>

            {/* Assistance Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-slate-800/60 dark:to-slate-800/30 border border-primary-100 dark:border-slate-700/60 shadow-xs space-y-2">
              <div className="flex items-center gap-2 font-bold text-xs text-primary-900 dark:text-primary-300">
                <LifeBuoy className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0" />
                <span>Precisa de Ajuda?</span>
              </div>
              <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                Consulte a documentação técnica ou entre em contacto com a equipa de suporte para
                dúvidas de integração.
              </p>
              <Link
                to="/support"
                className="inline-flex items-center gap-1.5 font-bold text-xs text-primary-700 dark:text-primary-400 hover:underline pt-0.5"
              >
                <span>Abrir Ticket de Suporte</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          <div className="min-h-[600px]">
            <Outlet context={{ application: appData }} />
          </div>
        </main>
      </div>
    </div>
  );
};
